/**
 * SnowBackground.tsx — React host for the PixiJS snow scene (tasks C01/C02).
 *
 *  - Renders a fixed, full-viewport, pointer-transparent layer BEHIND the
 *    UI (forms must stay interactive — the acceptance criterion).
 *  - Honors prefers-reduced-motion: static frame instead of animation, and
 *    reacts live to preference changes.
 *  - Degrades gracefully to a CSS gradient if WebGL/Canvas is unavailable.
 *
 * Performance (K04): the (large) PixiJS scene is loaded via a DYNAMIC
 * import — the initial bundle contains only the app UI, and the decorative
 * snow layer paints as soon as its own chunk arrives (the CSS gradient
 * shows in the meantime, so the lazy load is a pure enhancement).
 */
import { memo, useEffect, useRef, useState } from 'react';
import type { SnowScene } from './pixi/SnowScene';
import { readPrefersReducedMotion, subscribeReducedMotion } from './pixi/reducedMotion';

function SnowBackgroundInner() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [reduced, setReduced] = useState<boolean>(() => readPrefersReducedMotion());
  const [sceneFailed, setSceneFailed] = useState(false);

  useEffect(() => {
    return subscribeReducedMotion(setReduced);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let scene: SnowScene | null = null;
    let cancelled = false;

    (async () => {
      try {
        // K04: keep pixi.js out of the initial bundle (see perf-bundle
        // tests). Type-only import above is erased at compile time; only
        // this dynamic import pulls the runtime chunk.
        const { SnowScene: Scene } = await import('./pixi/SnowScene');
        if (cancelled) return;
        const next = new Scene(host, { static: reduced });
        await next.init();
        if (cancelled) {
          next.destroy();
          return;
        }
        scene = next;
        setSceneFailed(false);
      } catch {
        // No usable WebGL/Canvas context — keep the CSS gradient fallback.
        if (!cancelled) setSceneFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      scene?.destroy();
      scene = null;
    };
  }, [reduced]);

  return (
    <div
      ref={hostRef}
      className={sceneFailed ? 'snow-background snow-background--fallback' : 'snow-background'}
      aria-hidden="true"
    />
  );
}

/**
 * K04: the host has no props — App re-renders on session/user/navigation
 * changes, and each such re-render must not re-render the snow host either.
 */
export const SnowBackground = memo(SnowBackgroundInner);
